import { NavigationConfiguration } from 'common/apiService';

const buildQueryString = (params: Record<string, unknown>): string => Object.entries(params)
  .filter(([, value]) => !!value)
  .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
  .join('&');

// api_url is a backend endpoint (e.g. "api/contents?categories=abc&order=latest");
// its querystring carries the get_by_filter params that must be re-applied to the frontend route.
const resolveQueryParams = (api_url: string | undefined, fallbackParams: Record<string, unknown>): Record<string, unknown> => {
  if (!api_url) {
    return fallbackParams;
  }

  const [, queryString] = api_url.split('?');
  return queryString ? Object.fromEntries(new URLSearchParams(queryString)) : {};
};

const withQueryParams = (baseUrl: string, navigation_configuration?: NavigationConfiguration, slug?: string): string => {
  const { api_url, ...rest } = navigation_configuration ?? {};
  const slugUrl = slug ? `/${slug}` : '';
  const queryString = buildQueryString(resolveQueryParams(api_url, rest as Record<string, unknown>));

  return queryString ? `${baseUrl}?${queryString}` : baseUrl + slugUrl;
};

export default function getNavigationUrl(
  navigation_configuration?: NavigationConfiguration,
  slug?: string,
  short_id?: string,
  widget_id?: string,
): string | undefined {
  switch (navigation_configuration?.navigation_type) {
    case 'none':
      return '#';

    case 'external_route':
    case 'internal_route':
      return navigation_configuration.url;

    case 'tag':
      return `/contents?tags=${navigation_configuration.tag_id}`;

    case 'get_by_filter':
      return withQueryParams('/contents', navigation_configuration, slug);

    case 'content':
      return withQueryParams(`/contents/${short_id ? short_id : navigation_configuration.content_id}`, navigation_configuration, slug);

    case 'category':
      return withQueryParams(`/categories/${navigation_configuration.category_id}`, navigation_configuration, slug);

    case 'person':
      return withQueryParams(`/artists/${navigation_configuration.person_id}`, navigation_configuration, slug);

    case 'content_short':
      return withQueryParams(`/shorts/${short_id}`, navigation_configuration, slug);

    case 'live_channel':
      return '#';

    case 'widget':
      return withQueryParams(`/widget/${widget_id}`);

    default:
      return withQueryParams(navigation_configuration?.navigation_type ?? '', navigation_configuration, slug);
  }
}
